import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import React, { useMemo } from 'react';
import { ISbStoryData } from 'storyblok';

import { WORK_PAGE_TYPE } from '../../../constants';
import {
  VARIANT_COLOURS,
  VARIANT_SIZES_SORT_ORDER,
} from '../../../constants/variants';
import { VariantOption } from '../../../types/Analytics';
import {
  ArtistStoryblok,
  ProductV2Storyblok,
} from '../../../types/generated-storyblok';
import type { VariantOptions } from '../../../types/Products';
import type { ProductPurchaseDetails } from '../../../types/Purchasing';
import { TrackProductVariantSwitchClicked } from '../../../utils/analytics';
import { renderArtistsNamesFromPageContent } from '../../../utils/artists';
import { getVariantOptions } from '../../../utils/variants';
import { VariantIcon } from '../VariantIcon';
import type { ProductDetailsProps } from './types';

type ProductVariantGroupProps = {
  product: ProductType;
  variantOptions: VariantOptions;
  slug?: string;
};

const trackVariantOption = (
  product: ProductType,
  variant: VariantProduct,
  idx: number,
  variantOption: string,
) => {
  TrackProductVariantSwitchClicked({
    page: WORK_PAGE_TYPE,
    context: 'variant switch',
    variantOption: variantOption as VariantOption,
    position: idx,
    sourceProduct: {
      id: product.id[0].id,
      artistName: renderArtistsNamesFromPageContent(product.productArtist),
      editionSize: product.editionSize ? parseInt(product.editionSize, 10) : 0,
      releaseType: product.releaseType,
      title: product.title,
      type: 'Product',
      medium: product.medium ? product.medium[0] : undefined,
      releaseDate: product.launchDate,
      pricesAndCurrencies: product.pricesAndAvailability?.pricesAndCurrencies,
    },
    targetProduct: {
      id:
        variant.content.component === 'product_v2'
          ? (variant.content as unknown as ProductV2Storyblok).shopifyId
          : variant.content.id[0].id,
      artistName:
        variant.content.component === 'product_v2'
          ? (
              (variant.content as unknown as ProductV2Storyblok).artists[0]
                .artist as ISbStoryData<ArtistStoryblok>
            )?.name
          : (variant.content.productArtist[0] as ResolvedRelatedArtist).artist
              .name,
      editionSize: variant.content.editionSize
        ? parseInt(variant.content.editionSize, 10)
        : 0,
      releaseType: variant.content.releaseType,
      title: variant.content.title,
      type: 'Product',
      medium: variant.content.medium ? variant.content.medium[0] : undefined,
      releaseDate: variant.content.launchDate,
      pricesAndCurrencies:
        variant.content.pricesAndAvailability?.pricesAndCurrencies,
    },
  });
};

export const ProductVariantGroup: React.FC<React.PropsWithChildren> = ({
  children,
}) => (
  <div className="flex flex-row flex-wrap items-center justify-center gap-3 px-4">
    {children}
  </div>
);

const ProductVariantGroup_Sizes: React.FC<ProductVariantGroupProps> = ({
  product,
  slug,
  variantOptions,
}) => {
  if (variantOptions.sizes.length === 0) return null;

  const { t } = useTranslation('product');
  const sizes = variantOptions.sizes.sort(
    (a, b) =>
      VARIANT_SIZES_SORT_ORDER[a.size.size] -
      VARIANT_SIZES_SORT_ORDER[b.size.size],
  );

  return (
    <ProductVariantGroup>
      {sizes.map(({ size, variant }, idx) => (
        <Link
          key={idx}
          href={`/${variant.full_slug}`}
          onClick={() => {
            trackVariantOption(product, variant, idx, 'size');
          }}
        >
          <VariantIcon
            active={variant.slug === slug}
            text={size.size.toUpperCase()}
            tooltipText={t(`variants.sizes.${size.size}`)}
          />
        </Link>
      ))}
    </ProductVariantGroup>
  );
};

const ProductVariantGroup_Colourways: React.FC<ProductVariantGroupProps> = ({
  product,
  slug,
  variantOptions,
}) => {
  if (variantOptions.colourways.length === 0) return null;

  const colourways = variantOptions.colourways.sort((a, b) =>
    a.variant.slug.localeCompare(b.variant.slug),
  );

  return (
    <ProductVariantGroup>
      {colourways.map(({ colourway, variant }, idx) => (
        <Link
          key={idx}
          href={`/${variant.full_slug}`}
          onClick={() => {
            trackVariantOption(product, variant, idx, 'colour');
          }}
        >
          <VariantIcon
            active={variant.slug === slug}
            backgroundColours={colourway.colours.map((c) => VARIANT_COLOURS[c])}
            tooltipText={colourway.name}
          />
        </Link>
      ))}
    </ProductVariantGroup>
  );
};

const ProductVariantGroup_Finishing: React.FC<ProductVariantGroupProps> = ({
  product,
  slug,
  variantOptions,
}) => {
  if (variantOptions.finishing.length <= 1) return null;

  const { t } = useTranslation('product');
  const finishings = variantOptions.finishing.sort((a, b) =>
    a.variant.slug.localeCompare(b.variant.slug),
  );

  return (
    <ProductVariantGroup>
      {finishings.map(({ handFinished, variant }, idx) => (
        <Link
          key={idx}
          href={`/${variant.full_slug}`}
          onClick={() => {
            trackVariantOption(product, variant, idx, 'finishing');
          }}
        >
          <VariantIcon
            active={variant.slug === slug}
            crossedOut={!handFinished}
            text="HF"
            tooltipText={
              handFinished
                ? t('variants.handFinishedYes')
                : t('variants.handFinishedNo')
            }
          />
        </Link>
      ))}
    </ProductVariantGroup>
  );
};

const ProductVariantGroup_Compositions: React.FC<ProductVariantGroupProps> = ({
  product,
  slug,
  variantOptions,
}) => {
  if (variantOptions.compositions.length === 0) return null;

  const { t } = useTranslation('product');
  const options = variantOptions.compositions.sort((a, b) =>
    a.composition.compositionNumber.localeCompare(
      b.composition.compositionNumber,
    ),
  );

  return (
    <ProductVariantGroup>
      {options.map(({ composition, variant }, idx) => (
        <Link
          key={idx}
          href={`/${variant.full_slug}`}
          onClick={() => {
            trackVariantOption(product, variant, idx, 'composition');
          }}
        >
          <VariantIcon
            active={variant.slug === slug}
            text={`#${composition.compositionNumber}`}
            tooltipText={t('variants.compositionNumber', {
              number: composition.compositionNumber,
            })}
          />
        </Link>
      ))}
    </ProductVariantGroup>
  );
};

const ProductVariantGroup_Dimensions: React.FC<ProductVariantGroupProps> = ({
  product,
  slug,
  variantOptions,
}) => {
  if (variantOptions.dimensions.length === 0) return null;

  const options = variantOptions.dimensions.sort((a, b) =>
    a.dimension.dimension.localeCompare(b.dimension.dimension),
  );

  return (
    <ProductVariantGroup>
      {options.map(({ dimension, variant }, idx) => (
        <Link
          key={idx}
          href={`/${variant.full_slug}`}
          onClick={() => {
            trackVariantOption(product, variant, idx, 'dimension');
          }}
        >
          <VariantIcon
            active={variant.slug === slug}
            text={dimension.dimension}
            tooltipText={dimension.name}
          />
        </Link>
      ))}
    </ProductVariantGroup>
  );
};

export const ProductVariants = <T extends ProductPurchaseDetails>({
  product,
  purchasing,
}: Omit<ProductDetailsProps<T>, 'onShowTaxDetails' | 'onShowFramingDetails'> &
  Partial<
    Pick<ProductDetailsProps<T>, 'onShowTaxDetails' | 'onShowFramingDetails'>
  >) => {
  const variants = product.productVariants?.[0]?.variants;

  if (!product.id || !variants || purchasing.product?.requiresUnlockCode) {
    return null;
  }

  const slug = purchasing.product?.slug;
  const variantOptions = useMemo(() => {
    return getVariantOptions(product);
  }, [product]);

  const displayProps: ProductVariantGroupProps = {
    product,
    slug,
    variantOptions,
  };

  return (
    <>
      <ProductVariantGroup_Sizes {...displayProps} />
      <ProductVariantGroup_Colourways {...displayProps} />
      <ProductVariantGroup_Finishing {...displayProps} />
      <ProductVariantGroup_Compositions {...displayProps} />
      <ProductVariantGroup_Dimensions {...displayProps} />
    </>
  );
};

export default ProductVariants;
